import { answerText, canBeSpoken, matchTranscript, type DictationField } from '@fieldops/shared'
import { useEffect, useMemo, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { sendDictation } from '../../lib/dictation.js'
import { useRecorder } from '../../lib/recorder.js'
import { Icon } from '../Icon.js'
import { DialogHeader, Modal } from '../Modal.js'
import { Button, Spinner } from '../ui.js'
import type { ReportAnswer, ReportAnswers, ReportField, ReportSuggestion } from './types.js'

/**
 * Talking a section in, by either of two engines.
 *
 * There is a microphone and there is the keyboard, and which one a technician
 * gets is not a preference — it is what the situation allows:
 *
 * - **The microphone** records a clip, sends it to be transcribed, and has a
 *   model sort the words onto this section's questions. It understands a sentence
 *   that wanders. It needs signal, it costs money per use, and the recording is
 *   kept on the job.
 * - **The keyboard** uses the phone's own dictation to fill the box below, and
 *   `matchTranscript` sorts the words here on the device. It understands less. It
 *   works in a subfloor with no bars, sends nothing anywhere, and is free.
 *
 * The screen says which one just ran, every time, because the difference is one a
 * technician is entitled to know and because the first version of this file
 * promised "nothing is uploaded" — a promise that must not survive the arrival of
 * a provider as decoration. Decision 61.
 *
 * When the cloud pass fails, the keyboard path is not a consolation prize: the
 * words usually came back before the mapping failed, so they are dropped into the
 * box and matched on the device. The technician sees suggestions either way.
 *
 * What does not change with the engine: nothing here writes an answer. Both
 * engines produce suggestions that land in the same review sheet, arriving ticked
 * and costing one tap to reject. Decision 55.
 */

export function DictationSheet({
  open,
  onClose,
  sectionName,
  sectionKey,
  fields,
  answers,
  responseId,
  cloud,
  onOffer,
}: {
  open: boolean
  onClose: () => void
  sectionName: string
  /** Scopes a cloud pass to this step's questions. */
  sectionKey?: string | null
  fields: ReportField[]
  answers: ReportAnswers
  /** Null when no report exists yet, which switches the microphone off. */
  responseId: string | null
  /** What the server says it can do, and what is left of today's allowance. */
  cloud?: { available: boolean; maxSeconds: number; remaining: number }
  /** Hands the matches to the review sheet. Nothing here writes an answer. */
  onOffer: (suggestions: ReportSuggestion[], runId: string | null) => void
}) {
  const [transcript, setTranscript] = useState('')
  /** Set once a pass has run, so the sheet can say which engine produced this. */
  const [ran, setRan] = useState<'cloud' | 'device' | null>(null)
  const [runId, setRunId] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  /** Suggestions from the cloud pass, which replace the local matching. */
  const [fromCloud, setFromCloud] = useState<ReportSuggestion[] | null>(null)

  const recorder = useRecorder({ maxSeconds: cloud?.maxSeconds ?? 120 })

  useEffect(() => {
    if (!open) return
    setTranscript('')
    setRan(null)
    setRunId(null)
    setNotice(null)
    setFailed(null)
    setFromCloud(null)
  }, [open])

  const askable = useMemo(() => fields.filter(canBeSpoken).map(toDictationField), [fields])

  // Matching is a pure function of the words, so it can run on every keystroke
  // and tick the questions off as they are said. That live feedback is the whole
  // reason the questions are on screen.
  const local = useMemo(() => matchTranscript(transcript, askable), [transcript, askable])

  const labels = new Map(fields.map((field) => [field.key, field.label]))

  const suggestions: ReportSuggestion[] =
    fromCloud ??
    (local.matches.map((match) => ({
      fieldKey: match.fieldKey,
      label: labels.get(match.fieldKey) ?? match.fieldKey,
      value: match.value,
      text: answerText(match.value),
      source: 'heard' as const,
      reason: match.reason,
    })) as ReportSuggestion[])

  const heard = new Map(suggestions.map((one) => [one.fieldKey, one]))
  const unheard = askable.filter((field) => !heard.has(field.key))
  // Counted against the questions on screen rather than against the suggestions,
  // because a pass over a form with no sections is scoped to the whole report and
  // can answer a question this step does not show. "Heard 6 of 5" is nonsense.
  const heardHere = askable.length - unheard.length

  // The microphone needs a report to hang the recording off, a provider at the
  // other end, and a browser that can record. Any of the three missing leaves the
  // keyboard, which needs none of them.
  const canRecord =
    cloud?.available === true && responseId !== null && recorder.supported && !sending

  const send = async (clip: { blob: Blob; filename: string; seconds: number }) => {
    if (responseId === null) return

    setSending(true)
    setFailed(null)
    setNotice(null)

    try {
      const outcome = await sendDictation(clip.blob, {
        responseId,
        sectionKey: sectionKey ?? null,
        seconds: clip.seconds,
        filename: clip.filename,
      })

      if (outcome.engine === 'cloud') {
        setTranscript(outcome.transcript)
        setFromCloud(
          outcome.suggestions.map((one) => ({
            ...one,
            text: answerText(one.value),
          })) as ReportSuggestion[],
        )
        setRunId(outcome.runId)
        setRan('cloud')
        return
      }

      /**
       * The cloud pass did not happen, or only half of it did.
       *
       * If the words came back, they go in the box and the on-device matcher
       * takes over — `fromCloud` stays null, so the memo above is what fills the
       * list. If they did not, the box is left for the keyboard and the reason is
       * shown. Neither case is an error state: the feature still works.
       */
      if (outcome.transcript) setTranscript(outcome.transcript)
      setFromCloud(null)
      setRan('device')
      setNotice(outcome.reason)
    } catch (cause) {
      // Only the refusals no engine would fix reach here.
      setFailed(cause instanceof Error ? cause.message : 'That recording did not go through.')
    } finally {
      setSending(false)
    }
  }

  const record = async () => {
    if (recorder.recording) {
      recorder.stop()
      return
    }

    setFromCloud(null)
    setRan(null)
    setNotice(null)
    const clip = await recorder.start()
    if (clip) await send(clip)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      labelledBy="dictation-sheet-title"
      className="max-w-[520px]"
    >
      <DialogHeader
        id="dictation-sheet-title"
        title={`Talk to fill in ${sectionName}`}
        description="Say it however you would say it to a mate. Read what comes back — neither way of listening is a mind reader."
        onClose={onClose}
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        {canRecord || recorder.recording ? (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => void record()}
              disabled={sending}
              aria-label={recorder.recording ? 'Stop recording' : 'Record what you say'}
              className={cn(
                'flex min-h-[var(--control-lg)] items-center justify-center gap-2.5 rounded-md px-4 text-[14px] font-semibold transition-colors',
                recorder.recording
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-brand-500 text-ink-900 hover:bg-brand-550 disabled:bg-ink-200 disabled:text-ink-500',
              )}
            >
              {sending ? (
                <Spinner className="size-4" />
              ) : (
                <Icon name="mic" className={recorder.recording ? 'animate-pulse' : undefined} />
              )}
              {sending
                ? 'Sending it up…'
                : recorder.recording
                  ? `Stop — ${recorder.seconds}s`
                  : 'Record what you say'}
            </button>

            <p className="text-xs leading-relaxed text-ink-500">
              {recorder.recording
                ? `Recording. It stops on its own at ${cloud?.maxSeconds ?? 120} seconds.`
                : `The recording is sent away to be turned into words, and is kept on the job. ${
                    cloud ? `${cloud.remaining} left today. ` : ''
                  }Type instead and everything stays on this phone.`}
            </p>
          </div>
        ) : null}

        {recorder.error ? (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-[12.5px] leading-relaxed text-red-700 ring-1 ring-red-200 ring-inset">
            {recorder.error}
          </p>
        ) : null}

        {failed ? (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-[12.5px] leading-relaxed text-red-800 ring-1 ring-red-200 ring-inset">
            {failed}
          </p>
        ) : null}

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold tracking-[0.03em] text-ink-500 uppercase">
            What you said
          </span>
          <textarea
            value={transcript}
            onChange={(event) => {
              setTranscript(event.target.value)
              // Typing is the device engine by definition, so a cloud result
              // stops standing in for words that have since been edited.
              setFromCloud(null)
              setRunId(null)
              if (event.target.value !== transcript) setRan(null)
            }}
            rows={4}
            placeholder="Occupied brick veneer, single storey slab, tiled roof, shed and carport."
            className="w-full resize-y rounded-lg bg-white px-3 py-2.5 text-[14px] leading-relaxed text-ink-900 ring-1 ring-ink-200 ring-inset outline-none focus:ring-2 focus:ring-brand-500"
          />
        </label>

        {/* Said out loud, and said differently depending on what happened, because
            the two engines make opposite promises and only one of them can be
            true at a time. */}
        <p className="text-xs leading-relaxed text-ink-500">
          {ran === 'cloud'
            ? 'Those words were transcribed off the recording and sorted by a language model. Check each one.'
            : ran === 'device'
              ? 'Sorted on this phone, word by word. Nothing was sent anywhere.'
              : canRecord
                ? 'Use the keyboard’s microphone in the box above and it stays on this phone. Record instead and it goes up to be transcribed.'
                : 'This stays on your phone. Nothing is uploaded and it works with no signal.'}
        </p>

        {notice ? (
          <p className="rounded-lg bg-ink-50 px-3 py-2 text-[12.5px] leading-relaxed text-ink-600 ring-1 ring-ink-200 ring-inset">
            {notice}
          </p>
        ) : null}

        {askable.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-bold tracking-[0.05em] text-ink-400 uppercase">
              {transcript.trim().length === 0
                ? 'What this section asks — say what you can, skip what you cannot'
                : `Heard ${heardHere} of ${askable.length}`}
            </span>

            {askable.map((field) => {
              const match = heard.get(field.key)

              return (
                <div
                  key={field.key}
                  className={cn(
                    'flex items-start gap-2.5 rounded-lg px-2.5 py-2 ring-1 ring-inset',
                    match ? 'bg-brand-50 ring-brand-200' : 'bg-white ring-ink-200',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'mt-px grid size-5 shrink-0 place-items-center rounded-[4px] text-[10.5px] font-bold',
                      match
                        ? 'bg-brand-600 text-white'
                        : 'bg-ink-100 text-ink-500 ring-1 ring-ink-200 ring-inset',
                    )}
                  >
                    {match ? '✓' : ''}
                  </span>
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[13px] leading-snug font-medium text-ink-800">
                      {labels.get(field.key) ?? field.key}
                    </span>
                    <span className="text-[11.5px] text-ink-500">
                      {match
                        ? replacementNote(answerText(match.value), answers[field.key])
                        : (field.dictationHint ?? '')}
                    </span>
                  </span>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-[13px] text-ink-600">
            Nothing in this section can be answered by talking. Photos, signatures and dates are
            tapped in.
          </p>
        )}

        {transcript.trim().length > 0 && suggestions.length === 0 ? (
          <p className="rounded-lg bg-ink-50 px-3 py-2 text-[12.5px] leading-relaxed text-ink-600 ring-1 ring-ink-200 ring-inset">
            None of that matched a question on this section. Try naming the answer the way the
            question does — the grey line under each one is an example.
          </p>
        ) : null}

        {suggestions.length > 0 && unheard.length > 0 ? (
          <p className="rounded-lg bg-ink-50 px-3 py-2 text-[12.5px] leading-relaxed text-ink-600 ring-1 ring-ink-200 ring-inset">
            {unheard.length === 1
              ? 'One question got nothing from that, and is left alone.'
              : `${unheard.length} questions got nothing from that, and are left alone.`}
          </p>
        ) : null}
      </div>

      <div className="flex items-center gap-2.5 border-t border-ink-200 px-4 py-3">
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          size="lg"
          block
          disabled={suggestions.length === 0}
          onClick={() => {
            onOffer(suggestions, runId)
            onClose()
          }}
        >
          {suggestions.length === 0
            ? 'Nothing matched yet'
            : suggestions.length === 1
              ? 'Review the one answer'
              : `Review these ${suggestions.length} answers`}
        </Button>
      </div>
    </Modal>
  )
}

/**
 * What a heard answer would do to the one already there.
 *
 * Said on this row rather than left to the review sheet, because a technician
 * re-reading a section aloud has no other way to see that they are about to talk
 * over an answer they typed earlier.
 */
function replacementNote(heard: string, current: ReportAnswer): string {
  const existing = answerText(current)
  if (existing.length === 0 || existing === heard) return heard
  return `${heard} — instead of ${existing}`
}

function toDictationField(field: ReportField): DictationField {
  return {
    key: field.key,
    label: field.label,
    type: field.type,
    options: Array.isArray(field.options) ? (field.options as string[]) : null,
    dictationHint: field.dictationHint,
  }
}
